import { useSelector, useDispatch } from 'react-redux';
import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { CONTROLLER_CONFIG, CONTROLLER_ORDER } from '@/lib/controllerConfig';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  IControllerType,
  IToolData,
  ILaserOrEndmill,
  IPostAttachment,
  IModelGroup,
} from '@/redux/post/post.d';
import {
  SPINDLE_SPEED_MIN,
  SPINDLE_SPEED_MAX,
  SPINDLE_SPEED_DEFAULT,
  toolHasBit,
} from '@/redux/post/post.constants';
import {
  selectProjectEditorData,
  setProjectEditorData,
} from '@/redux/post/post.slice';
import UnmappedFilesPanel from './components/UnmappedFilesPanel';
import { C2Editor } from './components/C2Editor';
import { C2AskShareTools } from './components/C2AskShareTools';
import { C2ApplyMultiSelect } from './components/C2ApplyMultiSelect';
import ConfiguredGroupsPanel from './components/ConfiguredGroupsPanel';
import { inferGroupName } from './utils/inferGroupName';
import {
  startEditing as wfStartEditing,
  updateToolDataDraft as wfUpdateToolDataDraft,
  answerShareTools as wfAnswerShareTools,
  completeToolData as wfCompleteToolData,
  setApplySelection as wfSetApplySelection,
  completeGroup as wfCompleteGroup,
  deleteGroup as wfDeleteGroup,
  deleteToolFromGroup as wfDeleteToolFromGroup,
  updateGroupName as wfUpdateGroupName,
  setFinalized as wfSetFinalized,
  resetWorkflow as wfResetWorkflow,
  addToolToGroup as wfAddToolToGroup,
  loadExistingGroups as wfLoadExistingGroups,
  editExistingGroup as wfEditExistingGroup,
  type GroupConfig,
  type ToolData,
} from '@/redux/modelToolMappingWorkflow/slice';
import type { RootState } from '@/redux/store';
import type { UnmappedFileItem } from './components/C1UnmappedList';
import ModelViewer from '@/components/ModelViewer';
import { FileNameChip } from './components/FileNameChip';

const getControllerFromFileName = (
  fileName: string
): IControllerType | null => {
  const prefixMatch = CONTROLLER_ORDER.find((controller) =>
    fileName.toLowerCase().startsWith(`${controller.toLowerCase()}_`)
  );
  if (prefixMatch) return prefixMatch;
  const fileExtension = fileName
    .toLowerCase()
    .substring(fileName.lastIndexOf('.'));
  const extensionMatch = CONTROLLER_ORDER.find((controller) => {
    const config = CONTROLLER_CONFIG[controller];
    return config.allowedFileTypes.includes(fileExtension);
  });
  return extensionMatch || null;
};

/** Maps a completed group's toolDatas to the lean IToolData shape used by ConfiguredGroupsPanel. */
const mapToLeanToolData = (g: GroupConfig) =>
  g.toolDatas && g.toolDatas.length > 0
    ? g.toolDatas.map((td, idx: number) => ({
        _id: `${g.groupName}-tool-${idx}`,
        modelId: g.fileIds?.[0] || '',
        laserOrEndmill: td.laserOrEndmill as ILaserOrEndmill,
        bitId: td.bitId,
        brandId: td.brandId,
        spindleSpeed: td.spindleSpeed,
      }))
    : [];

export default function ModelToolMapping({
  pageErrors,
}: {
  pageErrors: { [key: string]: string };
}) {
  // Suppress unused parameter warning
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  pageErrors;
  const projectEditorData = useSelector(selectProjectEditorData);
  const workflow = useSelector(
    (state: RootState) => state.modelToolMappingWorkflow
  );
  const dispatch = useDispatch();
  const [openAccordionItems, setOpenAccordionItems] = useState<string[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();

  // Open the current group's accordion (group row + its toolData row).
  const openGroupAccordion = () => {
    if (!workflow.derivedGroupName) return;
    setOpenAccordionItems((prev) =>
      Array.from(
        new Set([
          ...prev,
          workflow.derivedGroupName as string,
          `${workflow.derivedGroupName}-toolData`,
        ])
      )
    );
  };

  // Use existing types from Redux store
  type Model = IPostAttachment;
  type ModelGroup = IModelGroup;
  type CompletedGroup = GroupConfig;

  const getControllerIcon = (
    fileName: string
  ): React.ComponentType<{ className?: string }> | null => {
    const controller = getControllerFromFileName(fileName);
    if (!controller) return null;
    const config = CONTROLLER_CONFIG[controller];
    return config.icon ?? null;
  };

  const getControllerName = (fileName: string): string => {
    const controller = getControllerFromFileName(fileName);
    return controller ?? 'Unknown';
  };

  // Build unmapped files grouped by controller
  const unmappedFilesByController: Record<string, UnmappedFileItem[]> =
    useMemo(() => {
      const modelsInGroups = new Set<string>();
      // Exclude files already in persisted project groups
      projectEditorData.modelGroups.forEach((group: ModelGroup) => {
        group.modelIds.forEach((id: string) => modelsInGroups.add(id));
      });
      // Exclude files already added to in-session workflow groups
      workflow.completedGroups.forEach((g: CompletedGroup) => {
        (g.fileIds || []).forEach((id: string) => modelsInGroups.add(id));
      });
      const unmapped = projectEditorData.models.filter(
        (m: Model) => !modelsInGroups.has(m._id)
      );
      const grouped: Record<string, UnmappedFileItem[]> = {};
      unmapped.forEach((m: Model) => {
        const controller =
          getControllerFromFileName(m.fileName) || IControllerType.OTHER;
        if (!grouped[controller]) grouped[controller] = [];
        grouped[controller].push({
          id: m._id,
          fileName: m.fileName,
          controllerType: controller,
        });
      });
      return grouped;
    }, [
      projectEditorData.models,
      projectEditorData.modelGroups,
      workflow.completedGroups,
    ]);

  // Build completed files (files that already have tool data)
  const completedModels = useMemo(() => {
    const modelsInGroups = new Set<string>();
    // Include files already in persisted project groups
    projectEditorData.modelGroups.forEach((group: ModelGroup) => {
      group.modelIds.forEach((id: string) => modelsInGroups.add(id));
    });
    // Include files already added to in-session workflow groups
    workflow.completedGroups.forEach((g: CompletedGroup) => {
      (g.fileIds || []).forEach((id: string) => modelsInGroups.add(id));
    });
    const completed = projectEditorData.models.filter((m: Model) =>
      modelsInGroups.has(m._id)
    );
    return completed;
  }, [
    projectEditorData.models,
    projectEditorData.modelGroups,
    workflow.completedGroups,
  ]);

  const selectedModel = useMemo(() => {
    if (!workflow.selectedFileId) return null;
    return (
      projectEditorData.models.find(
        (m: Model) => m._id === workflow.selectedFileId
      ) || null
    );
  }, [workflow.selectedFileId, projectEditorData.models]);

  // const hasRemainingUnmapped = useMemo(() => {
  //   return Object.values(unmappedFilesByController).some(
  //     (arr) => arr.length > 0
  //   );
  // }, [unmappedFilesByController]);

  const hasOtherUnmappedFiles = useMemo(() => {
    // Check if there are any unmapped files other than the currently selected one
    const totalUnmapped = Object.values(unmappedFilesByController)
      .flat()
      .filter((f) => f.id !== workflow.selectedFileId).length;
    return totalUnmapped > 0;
  }, [unmappedFilesByController, workflow.selectedFileId]);

  // Check if current group already has any tool data (for LASER, only one tool allowed)
  const hasLaserTool = useMemo(() => {
    if (!workflow.derivedGroupName) return false;
    const group = workflow.completedGroups.find(
      (g: GroupConfig) => g.groupName === workflow.derivedGroupName
    );
    if (!group) return false;
    return (group.toolDatas || []).some(
      (tool: ToolData) => tool.laserOrEndmill === ILaserOrEndmill.LASER
    );
  }, [workflow.derivedGroupName, workflow.completedGroups]);

  // Current group already has at least one tool — drives "Add another tool to this toolpath gcode" vs "Add".
  const hasExistingTools = useMemo(() => {
    if (!workflow.derivedGroupName) return false;
    const group = workflow.completedGroups.find(
      (g: GroupConfig) => g.groupName === workflow.derivedGroupName
    );
    return !!group && (group.toolDatas || []).length > 0;
  }, [workflow.derivedGroupName, workflow.completedGroups]);

  const isActive =
    workflow.step === 'editing' ||
    workflow.step === 'askShareTools' ||
    workflow.step === 'applyToGroup';
  const isFinalized = workflow.step === 'finalized';

  const c2Draft = useMemo(() => {
    if (!selectedModel) return null;

    // Default values for a new ENDMILL tool (bit picked from the catalog)
    const baseDefaults = {
      laserOrEndmill: ILaserOrEndmill.ENDMILL,
      bitId: undefined,
      brandId: undefined,
      spindleSpeed: SPINDLE_SPEED_DEFAULT, // Default spindle speed for ENDMILL
    };

    // If toolDataDraft exists, use it
    if (workflow.toolDataDraft) {
      // LASER carries the selected laser's catalog bitId, but no endmill-only
      // fields (spindle speed).
      if (workflow.toolDataDraft.laserOrEndmill === ILaserOrEndmill.LASER) {
        return {
          laserOrEndmill: ILaserOrEndmill.LASER,
          bitId: workflow.toolDataDraft.bitId,
          brandId: workflow.toolDataDraft.brandId,
        };
      }
      return {
        laserOrEndmill: workflow.toolDataDraft
          .laserOrEndmill as ILaserOrEndmill,
        bitId: workflow.toolDataDraft.bitId,
        brandId: workflow.toolDataDraft.brandId,
        spindleSpeed:
          workflow.toolDataDraft.spindleSpeed ?? SPINDLE_SPEED_DEFAULT, // Default if not set
      };
    }

    // Return defaults
    return baseDefaults;
  }, [workflow.toolDataDraft, selectedModel]);

  const canCompleteGroup = useMemo(() => {
    const nameOk =
      !!workflow.derivedGroupName &&
      workflow.derivedGroupName.trim().length > 0;
    const group = workflow.completedGroups.find(
      (g: CompletedGroup) => g.groupName === workflow.derivedGroupName
    );
    // Already-committed tools in the group: every tool needs a bitId.
    const groupToolsOk =
      !!group &&
      Array.isArray(group.fileIds) &&
      group.fileIds.length >= 1 &&
      Array.isArray(group.toolDatas) &&
      group.toolDatas.length >= 1 &&
      group.toolDatas.every((tool: ToolData) => toolHasBit(tool));
    // Or a valid pending draft — Complete appends it then finishes. This is the
    // only path for LASER, which has no separate Add button.
    const draftOk =
      !!workflow.toolDataDraft &&
      toolHasBit(workflow.toolDataDraft) &&
      !!workflow.selectedFileId;
    return nameOk && (groupToolsOk || draftOk);
  }, [
    workflow.derivedGroupName,
    workflow.completedGroups,
    workflow.toolDataDraft,
    workflow.selectedFileId,
  ]);

  useEffect(() => {
    // Calculate unmapped files using ONLY workflow.completedGroups (source of truth)
    // Don't use projectEditorData.modelGroups as it syncs in a separate effect and causes race condition
    const modelsInGroups = new Set<string>();

    // Only check workflow.completedGroups - this updates immediately when groups are deleted
    workflow.completedGroups.forEach((g: CompletedGroup) => {
      (g.fileIds || []).forEach((id: string) => modelsInGroups.add(id));
    });

    const unmappedCount = projectEditorData.models.filter(
      (m: Model) => !modelsInGroups.has(m._id)
    ).length;
    const hasUnmapped = unmappedCount > 0;

    // Only auto-finalize when:
    // 1. No unmapped files remain
    // 2. Not currently editing
    // 3. Have at least one completed group
    // 4. Step is not already finalized
    if (
      !hasUnmapped &&
      workflow.step !== 'editing' &&
      workflow.step !== 'finalized' &&
      workflow.completedGroups.length > 0
    ) {
      dispatch(wfSetFinalized());
    }
  }, [
    workflow.step,
    workflow.completedGroups,
    projectEditorData.models,
    dispatch,
  ]);

  // Reset workflow and load existing modelGroups on mount (for editing existing posts)
  useEffect(() => {
    // Always reset workflow first to clear any stale data
    dispatch(wfResetWorkflow());

    if (
      projectEditorData.modelGroups &&
      projectEditorData.modelGroups.length > 0
    ) {
      // Convert modelGroups back to workflow format
      const existingGroups: GroupConfig[] = projectEditorData.modelGroups.map(
        (group) => {
          // Get controller type from first model in group
          const firstModelId = group.modelIds[0];
          const firstModel = projectEditorData.models.find(
            (m) => m._id === firstModelId
          );
          const controllerType = firstModel
            ? getControllerFromFileName(firstModel.fileName)
            : IControllerType.OTHER;

          return {
            groupName: group.name,
            controllerType: controllerType || IControllerType.OTHER,
            fileIds: group.modelIds,
            toolDatas: group.toolData.map((tool) => ({
              modelId: tool.modelId,
              laserOrEndmill: tool.laserOrEndmill,
              // On EDIT, carry the lean bit reference. Old un-backfilled posts
              // have no bitId -> picker stays empty and the user must re-pick.
              bitId: tool.bitId,
              brandId: tool.brandId,
              spindleSpeed: tool.spindleSpeed,
            })),
          };
        }
      );

      dispatch(wfLoadExistingGroups(existingGroups));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Sync workflow completedGroups to projectEditorData.modelGroups
  useEffect(() => {
    const modelGroups: IModelGroup[] = workflow.completedGroups.map(
      (group) => ({
        _id: Date.now().toString() + Math.random(),
        name: group.groupName,
        modelIds: group.fileIds,
        toolData: group.toolDatas
          .filter((tool: ToolData): tool is ToolData & { modelId: string } =>
            Boolean(tool.modelId)
          )
          .map((tool: ToolData & { modelId: string }): IToolData => {
            // Ensure we use enum values, not keys
            // The workflow might store "ENDMILL" but we need "Endmill"
            let laserOrEndmill: ILaserOrEndmill;
            if (
              tool.laserOrEndmill === 'ENDMILL' ||
              tool.laserOrEndmill === ILaserOrEndmill.ENDMILL
            ) {
              laserOrEndmill = ILaserOrEndmill.ENDMILL;
            } else if (
              tool.laserOrEndmill === 'LASER' ||
              tool.laserOrEndmill === ILaserOrEndmill.LASER
            ) {
              laserOrEndmill = ILaserOrEndmill.LASER;
            } else {
              laserOrEndmill = tool.laserOrEndmill as ILaserOrEndmill;
            }

            // Lean tool row: a catalog bit reference for both kinds — Endmill
            // also carries an optional spindle speed; Laser carries only the
            // selected laser's bitId.
            return {
              _id: Date.now().toString() + Math.random(),
              modelId: tool.modelId,
              laserOrEndmill,
              bitId: tool.bitId,
              brandId: tool.brandId,
              ...(laserOrEndmill === ILaserOrEndmill.ENDMILL
                ? { spindleSpeed: tool.spindleSpeed }
                : {}),
            };
          }),
        createdAt: new Date().toISOString(),
      })
    );

    dispatch(
      setProjectEditorData({
        modelGroups,
      })
    );
  }, [workflow.completedGroups, dispatch]);

  // Early return after all hooks
  if (!projectEditorData.models || projectEditorData.models.length === 0) {
    return (
      <div className="h-full flex flex-col font-tertiary">
        <Card className="h-full w-full max-w-6xl mx-auto flex flex-col">
          <CardContent className="p-6 overflow-y-auto flex-1">
            <div className="h-full flex items-center justify-center">
              <div className="text-center space-y-6 max-w-2xl">
                <h3 className="text-sm font-medium">
                  No Gcode Files Available to Map Tools
                </h3>
                <p className="text-muted-foreground font-secondary">
                  No Gcode files were uploaded. Please upload Gcode files before
                  proceeding with tool Assignment.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={() => {
                      const newSearchParams = new URLSearchParams(searchParams);
                      newSearchParams.set('page', '2');
                      setSearchParams(newSearchParams);
                    }}
                    className="flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Upload Gcode Files
                  </Button>
                  <Button
                    onClick={() => {
                      window.location.reload();
                    }}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Refresh Page
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <div className="flex-1 min-h-0">
        {workflow.step === 'idle' ? (
          // Show only the file selection panel when no file is selected
          <div className="max-w-5xl mx-auto w-full h-full">
            <UnmappedFilesPanel
              availableModels={Object.values(unmappedFilesByController)
                .flat()
                .map((f) => ({ _id: f.id, fileName: f.fileName }))}
              selectedModelIds={[]}
              onModelSelection={(selectedValues: string[]) => {
                const last = selectedValues[selectedValues.length - 1];
                const selected = projectEditorData.models.find(
                  (m: Model) => m._id === last
                );
                if (!selected) return;
                const groupName = inferGroupName(selected.fileName);
                dispatch(
                  wfStartEditing({
                    fileId: selected._id,
                    fileName: selected.fileName,
                    groupName,
                  })
                );
                // Auto-expand the tool data section when entering editor mode
                setOpenAccordionItems((prev) => {
                  const next = new Set(prev);
                  next.add(groupName);
                  next.add(`${groupName}-toolData`);
                  return Array.from(next);
                });
              }}
              getControllerIcon={getControllerIcon}
              getControllerName={getControllerName}
              completedModels={completedModels.map((m: Model) => ({
                _id: m._id,
                fileName: m.fileName,
              }))}
              completedGroups={workflow.completedGroups.map(
                (g: CompletedGroup) => ({
                  _id: g.groupName,
                  name: g.groupName,
                  modelIds: g.fileIds || [],
                  toolData: g.toolDatas || [],
                })
              )}
              onDeleteGroup={(groupId: string) =>
                dispatch(wfDeleteGroup({ groupName: groupId }))
              }
              allModels={projectEditorData.models.map((m: Model) => ({
                _id: m._id,
                fileName: m.fileName,
              }))}
            />
          </div>
        ) : (
          // Show the full layout when a file is selected
          <div className="max-w-8xl mx-auto w-full h-full flex flex-col sm:flex-row gap-4">
            {/* Left section: Form and Preview */}
            {!isFinalized && (
              <div className="flex-1 flex flex-col gap-2 sm:h-full sm:min-h-0">
                {/* C2 */}
                {isActive && selectedModel && (
                  <div className="flex-shrink-0">
                    <div className="gap-4">
                      {workflow.step === 'editing' && c2Draft && (
                        <div>
                          <C2Editor
                            draft={c2Draft}
                            onRemove={() => dispatch(wfResetWorkflow())}
                            selectedModel={selectedModel}
                            hasExistingTools={hasExistingTools}
                            onChange={(d) => {
                              // Lean draft: Endmill carries a catalog bitId
                              // (+ optional spindle speed); Laser carries the
                              // selected laser's bitId (no spindle speed).
                              const toolData: ToolData = {
                                modelId: selectedModel._id,
                                laserOrEndmill: d.laserOrEndmill,
                                bitId: d.bitId,
                                brandId: d.brandId,
                                ...(d.laserOrEndmill === ILaserOrEndmill.ENDMILL
                                  ? { spindleSpeed: d.spindleSpeed }
                                  : {}),
                              };
                              dispatch(wfUpdateToolDataDraft(toolData));
                            }}
                            // onAdd: inherent multi-branch handler (laser guard, endmill validation, spindle range check)
                            // fallow-ignore-next-line complexity
                            onAdd={() => {
                              // Check if trying to add a second LASER tool
                              if (
                                c2Draft &&
                                c2Draft.laserOrEndmill ===
                                  ILaserOrEndmill.LASER &&
                                hasLaserTool
                              ) {
                                toast.error('Cannot add multiple LASER tools', {
                                  description:
                                    'Only one LASER tool is allowed per group',
                                });
                                return;
                              }

                              // Laser requires the user to pick which laser to use.
                              if (
                                c2Draft &&
                                c2Draft.laserOrEndmill ===
                                  ILaserOrEndmill.LASER &&
                                !c2Draft.bitId
                              ) {
                                toast.error('Laser is required', {
                                  description:
                                    'Please select which laser to use from the catalog',
                                });
                                return;
                              }

                              // Endmill requires a catalog bit before adding.
                              if (
                                c2Draft &&
                                c2Draft.laserOrEndmill ===
                                  ILaserOrEndmill.ENDMILL
                              ) {
                                if (!c2Draft.bitId) {
                                  toast.error('Bit is required', {
                                    description:
                                      'Please select a bit from the catalog for this Endmill tool',
                                  });
                                  return;
                                }

                                // If a spindle speed is provided, validate range.
                                if (
                                  c2Draft.spindleSpeed !== undefined &&
                                  c2Draft.spindleSpeed !== null
                                ) {
                                  const speed = Number(c2Draft.spindleSpeed);
                                  if (
                                    speed < SPINDLE_SPEED_MIN ||
                                    speed > SPINDLE_SPEED_MAX
                                  ) {
                                    toast.error('Invalid Spindle Speed', {
                                      description: `Spindle speed must be between ${SPINDLE_SPEED_MIN} and ${SPINDLE_SPEED_MAX} RPM`,
                                    });
                                    return;
                                  }
                                }
                              }

                              // Persist latest draft and append to current group without leaving editing
                              if (c2Draft) {
                                // Lean draft: Endmill carries bitId (+ optional
                                // spindle speed); Laser carries the selected
                                // laser's bitId (no spindle speed).
                                const toolData: ToolData = {
                                  modelId: selectedModel._id,
                                  laserOrEndmill: c2Draft.laserOrEndmill,
                                  bitId: c2Draft.bitId,
                                  brandId: c2Draft.brandId,
                                  ...(c2Draft.laserOrEndmill ===
                                  ILaserOrEndmill.ENDMILL
                                    ? { spindleSpeed: c2Draft.spindleSpeed }
                                    : {}),
                                };
                                dispatch(wfUpdateToolDataDraft(toolData));
                              }
                              const controllerType =
                                getControllerFromFileName(
                                  selectedModel.fileName
                                ) || IControllerType.OTHER;
                              dispatch(wfAddToolToGroup({ controllerType }));

                              if (workflow.derivedGroupName) {
                                setOpenAccordionItems((prev) => {
                                  const next = new Set(prev);
                                  next.add(workflow.derivedGroupName as string);
                                  next.add(
                                    `${workflow.derivedGroupName}-toolData`
                                  );
                                  return Array.from(next);
                                });
                              }
                            }}
                            onComplete={() => {
                              // A pending draft (one that wfCompleteGroup would
                              // append) must have a catalog bit selected — a bit
                              // for Endmill, a laser for Laser.
                              if (
                                workflow.toolDataDraft &&
                                !toolHasBit(workflow.toolDataDraft)
                              ) {
                                const isLaser =
                                  workflow.toolDataDraft.laserOrEndmill ===
                                  ILaserOrEndmill.LASER;
                                toast.error(
                                  isLaser
                                    ? 'Laser is required'
                                    : 'Bit is required',
                                  {
                                    description: isLaser
                                      ? 'Please select which laser to use from the catalog'
                                      : 'Please select a bit from the catalog for this Endmill tool',
                                  }
                                );
                                return;
                              }
                              // Check if we have tools already added to the group
                              const currentGroup =
                                workflow.completedGroups.find(
                                  (g) =>
                                    g.groupName === workflow.derivedGroupName
                                );
                              // A valid pending draft counts too — it's
                              // preserved through the share flow and committed on
                              // complete (the only path for LASER, no Add button).
                              const hasToolsInGroup =
                                (currentGroup &&
                                  currentGroup.toolDatas &&
                                  currentGroup.toolDatas.length > 0) ||
                                (!!workflow.toolDataDraft &&
                                  toolHasBit(workflow.toolDataDraft));

                              // If there are other unmapped files and we have tools, show the Yes/No question page
                              if (hasOtherUnmappedFiles && hasToolsInGroup) {
                                // Transition to askShareTools step to show Yes/No question
                                dispatch(wfCompleteToolData());
                                openGroupAccordion();
                              } else {
                                // Complete immediately (no other files to share with)
                                const controllerType =
                                  getControllerFromFileName(
                                    selectedModel.fileName
                                  ) || IControllerType.OTHER;
                                dispatch(wfCompleteGroup({ controllerType }));
                                openGroupAccordion();
                              }
                            }}
                            canComplete={canCompleteGroup}
                          />
                        </div>
                      )}
                      {workflow.step === 'askShareTools' && (
                        <div>
                          <C2AskShareTools
                            onYes={() => {
                              // User answered Yes - go to file selection
                              dispatch(wfAnswerShareTools(true));
                            }}
                            onNo={() => {
                              // User answered No - complete the group immediately
                              const controllerType =
                                getControllerFromFileName(
                                  selectedModel.fileName
                                ) || IControllerType.OTHER;
                              dispatch(wfAnswerShareTools(false));
                              dispatch(wfCompleteGroup({ controllerType }));
                              if (workflow.derivedGroupName) {
                                setOpenAccordionItems((prev) => {
                                  const next = new Set(prev);
                                  next.add(workflow.derivedGroupName as string);
                                  next.add(
                                    `${workflow.derivedGroupName}-toolData`
                                  );
                                  return Array.from(next);
                                });
                              }
                            }}
                          />
                        </div>
                      )}
                      {workflow.step === 'applyToGroup' && (
                        <div>
                          <C2ApplyMultiSelect
                            candidates={Object.values(unmappedFilesByController)
                              .flat()
                              .filter((f) => f.id !== selectedModel._id)}
                            selected={workflow.applyToFileIds}
                            onChange={(ids) =>
                              dispatch(wfSetApplySelection(ids))
                            }
                            onComplete={() => {
                              const handleCompleteGroup = () => {
                                dispatch(
                                  wfCompleteGroup({
                                    controllerType:
                                      getControllerFromFileName(
                                        selectedModel.fileName
                                      ) || IControllerType.OTHER,
                                  })
                                );
                                if (workflow.derivedGroupName) {
                                  setOpenAccordionItems((prev) => {
                                    const next = new Set(prev);
                                    next.add(
                                      workflow.derivedGroupName as string
                                    );
                                    next.add(
                                      `${workflow.derivedGroupName}-toolData`
                                    );
                                    return Array.from(next);
                                  });
                                }
                              };
                              handleCompleteGroup();
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Preview section below form - Takes remaining height */}
                {selectedModel && !isFinalized && (
                  <div className="flex-1 min-h-0">
                    <div className="relative h-full border rounded-md shadow-sm overflow-hidden">
                      <ModelViewer
                        modelURL={selectedModel.url}
                        enableGrid
                        backgroundColor="white"
                        gridColor="gray"
                        modelColor="green"
                        boundingBoxColor="gray"
                        dTextColor="black"
                      />
                      {/* Floating file name chip at bottom center */}
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 shadow-lg">
                        <FileNameChip
                          fileName={selectedModel.fileName}
                          onRemove={() => dispatch(wfResetWorkflow())}
                          size="sm"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Groups Panel - Centered when finalized, right-aligned otherwise */}
            {/* ponytail: still animates width (layout) once per finalize on a RARE surface; transform-based morph if it ever janks */}
            <div
              className={`sm:h-full transition-[width] duration-300 ease-in-out ${
                isFinalized ? 'w-full flex justify-center' : 'w-full sm:w-1/2'
              }`}
            >
              {isFinalized ? (
                <div className="w-full sm:w-2/3 lg:w-1/2">
                  <ConfiguredGroupsPanel
                    modelGroups={
                      isFinalized
                        ? workflow.completedGroups.map((g: CompletedGroup) => ({
                            _id: g.groupName,
                            name: g.groupName,
                            modelIds: g.fileIds || [],
                            toolData: mapToLeanToolData(g),
                          }))
                        : // During editing, only show current group's tool data
                          workflow.completedGroups
                            .filter(
                              (g: CompletedGroup) =>
                                g.groupName === workflow.derivedGroupName
                            )
                            .map((g: CompletedGroup) => ({
                              _id: g.groupName,
                              name: g.groupName,
                              modelIds: [], // Hide files during editing
                              toolData: mapToLeanToolData(g),
                            }))
                    }
                    openAccordionItems={openAccordionItems}
                    setOpenAccordionItems={setOpenAccordionItems}
                    handleDeleteModelGroup={(groupId: string) =>
                      dispatch(wfDeleteGroup({ groupName: groupId }))
                    }
                    deleteToolData={(groupId: string, toolIndex: string) =>
                      dispatch(
                        wfDeleteToolFromGroup({
                          groupName: groupId,
                          toolIndex: Number(toolIndex),
                        })
                      )
                    }
                    updateGroupName={(
                      oldGroupName: string,
                      newGroupName: string
                    ) =>
                      dispatch(
                        wfUpdateGroupName({ oldGroupName, newGroupName })
                      )
                    }
                    onEditGroup={(groupId: string) => {
                      dispatch(wfEditExistingGroup({ groupName: groupId }));
                      // Auto-expand the tool data section when editing existing group
                      setOpenAccordionItems((prev) => {
                        const next = new Set(prev);
                        next.add(groupId);
                        next.add(`${groupId}-toolData`);
                        return Array.from(next);
                      });
                    }}
                    models={projectEditorData.models}
                    getControllerIcon={getControllerIcon}
                    className="min-h-0 h-full overflow-y-auto"
                    isWorkflowFinalized={isFinalized}
                    showSimplifiedView={!isFinalized}
                    selectedFileName={selectedModel?.fileName}
                  />
                </div>
              ) : (
                <ConfiguredGroupsPanel
                  modelGroups={workflow.completedGroups
                    .filter(
                      (g: CompletedGroup) =>
                        g.groupName === workflow.derivedGroupName
                    )
                    .map((g: CompletedGroup) => ({
                      _id: g.groupName,
                      name: g.groupName,
                      modelIds: [], // Hide files during editing
                      toolData: mapToLeanToolData(g),
                    }))}
                  openAccordionItems={openAccordionItems}
                  setOpenAccordionItems={setOpenAccordionItems}
                  handleDeleteModelGroup={(groupId: string) =>
                    dispatch(wfDeleteGroup({ groupName: groupId }))
                  }
                  deleteToolData={(groupId: string, toolIndex: string) =>
                    dispatch(
                      wfDeleteToolFromGroup({
                        groupName: groupId,
                        toolIndex: Number(toolIndex),
                      })
                    )
                  }
                  updateGroupName={(
                    oldGroupName: string,
                    newGroupName: string
                  ) =>
                    dispatch(wfUpdateGroupName({ oldGroupName, newGroupName }))
                  }
                  onEditGroup={(groupId: string) => {
                    dispatch(wfEditExistingGroup({ groupName: groupId }));
                    // Auto-expand the tool data section when editing existing group
                    setOpenAccordionItems((prev) => {
                      const next = new Set(prev);
                      next.add(groupId);
                      next.add(`${groupId}-toolData`);
                      return Array.from(next);
                    });
                  }}
                  models={projectEditorData.models}
                  getControllerIcon={getControllerIcon}
                  className="min-h-0 h-full overflow-y-auto"
                  isWorkflowFinalized={isFinalized}
                  showSimplifiedView={!isFinalized}
                  selectedFileName={selectedModel?.fileName}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
